import Phaser from 'phaser';
import { getDialogueById } from '../data/dialogues';
import { Quest, getZoneQuestOrder } from '../data/quests';
import { Interactable } from '../interfaces/Interactable';
import { Hazard } from '../objects/Hazard';
import { Player } from '../objects/Player';
import { DebugOverlay } from '../ui/DebugOverlay';
import { MobileControls } from '../ui/MobileControls';
import { QuestPanel } from '../ui/QuestPanel';
import { ScoreHUD } from '../ui/ScoreHUD';
import { setZoneHeaderVisible } from '../ui/ZoneHudHeader';
import { SaveSystem } from './SaveSystem';
import { AudioSystem } from './AudioSystem';
import { DialogueSystem } from './DialogueSystem';
import { InputSystem } from './InputSystem';
import { InteractionSystem } from './InteractionSystem';
import { QuestSystem } from './QuestSystem';
import { ResponsiveSystem } from './ResponsiveSystem';
import { ScoreSystem } from './ScoreSystem';

export interface ObstacleRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class ZoneCoreSystems {
  readonly scene: Phaser.Scene;
  readonly input: InputSystem;
  readonly dialogue: DialogueSystem;
  readonly quest: QuestSystem;
  readonly score: ScoreSystem;

  private interaction?: InteractionSystem;
  private scoreHUD?: ScoreHUD;
  private questPanel?: QuestPanel;
  private mobileControls?: MobileControls;
  private debugOverlay?: DebugOverlay;
  private player?: Player;
  private obstacles?: Phaser.Physics.Arcade.StaticGroup;
  private currentZone = 1;
  private lastAutosaveTime = 0;
  private lastSavedX?: number;
  private lastSavedY?: number;
  private interactionGate?: () => boolean;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.input = new InputSystem(scene);
    this.dialogue = new DialogueSystem(scene, this.input);

    this.scoreHUD = new ScoreHUD(scene);
    this.score = ScoreSystem.fromSave((delta, _total, silent) => {
      this.scoreHUD?.updateScore(this.score.getScore());
      if (silent) return;
      if (delta > 0) AudioSystem.playSfx('SCORE_UP');
      else if (delta < 0) AudioSystem.playSfx('ERROR');
      this.scoreHUD?.showDelta(delta);
    });

    this.quest = new QuestSystem(() => this.refreshQuestPanel());
  }

  initPlayer(x: number, y: number): Player {
    // Posisi zona = titik tengah lama; player memakai origin kaki
    this.player = new Player(this.scene, x, y + 40);
    this.interaction = new InteractionSystem(
      this.scene,
      this.player,
      this.dialogue,
      this.quest,
      this.score,
    );
    this.savePlayerProgress(true);
    return this.player;
  }

  setupHUD(zone: number): void {
    this.currentZone = zone;
    this.scoreHUD?.create(this.score.getScore());
    this.refreshQuestPanel(zone);
    this.debugOverlay = new DebugOverlay(this.scene);
    this.debugOverlay.create();
  }

  setupMobileControls(): void {
    if (!ResponsiveSystem.shouldShowTouchControls(this.scene)) return;
    this.mobileControls = new MobileControls(this.scene, this.input);
    this.mobileControls.create();
    this.scene.data.set('setMobileControlsVisible', (visible: boolean) => {
      this.setMobileControlsVisible(visible);
    });
  }

  getPlayer(): Player {
    if (!this.player) throw new Error('Player not initialized — call initPlayer() first');
    return this.player;
  }

  registerInteractables(items: Interactable[]): void {
    this.interaction?.registerAll(items);
  }

  setInteractionGate(gate?: () => boolean): void {
    this.interactionGate = gate;
  }

  setupObstacles(rects: ObstacleRect[]): void {
    this.obstacles = this.scene.physics.add.staticGroup();
    rects.forEach((r) => {
      const block = this.scene.add.rectangle(r.x, r.y, r.width, r.height, 0x000000, 0);
      this.obstacles!.add(block);
    });
    if (this.player) {
      this.scene.physics.add.collider(this.player, this.obstacles);
    }
  }

  setupHazards(hazards: Hazard[]): void {
    const player = this.getPlayer();
    hazards.forEach((hazard) => {
      this.scene.physics.add.overlap(player, hazard, () => {
        hazard.onPlayerOverlap(player, this.score);
      });
    });
  }

  removeObstacleNear(x: number, y: number, tolerance = 30): void {
    if (!this.obstacles) return;
    this.obstacles.getChildren().forEach((child) => {
      const rect = child as Phaser.GameObjects.Rectangle;
      if (Math.abs(rect.x - x) <= tolerance && Math.abs(rect.y - y) <= tolerance) {
        rect.destroy();
      }
    });
  }

  /** Hapus beberapa penghalang sekaligus (mis. buka koridor exit zona). */
  removeObstaclesAtCenters(centers: readonly { x: number; y: number }[], tolerance = 50): void {
    centers.forEach(({ x, y }) => this.removeObstacleNear(x, y, tolerance));
  }

  showIntroDialogue(dialogueId: string, onComplete?: () => void): void {
    const dialogue = getDialogueById(dialogueId);
    if (dialogue) {
      this.mobileControls?.setVisible(false);
      this.dialogue.show(dialogue, () => {
        this.mobileControls?.setVisible(true);
        onComplete?.();
      });
    }
  }

  refreshQuestPanel(zone?: number): void {
    const z = zone ?? this.currentZone;
    const order = getZoneQuestOrder(z);
    const current = order ? this.quest.syncZoneQuestChain(order) : null;
    const quests: Quest[] = order
      ? order
        .map((id) => this.quest.getQuestById(id))
        .filter((q): q is Quest => q !== undefined)
      : this.quest.getQuestsByZone(z);
    this.questPanel?.show(quests, current);
  }

  setQuestPanel(panel: QuestPanel): void {
    this.questPanel = panel;
  }

  update(_time: number, delta: number): void {
    const player = this.getPlayer();
    this.input.update();
    this.dialogue.update();
    if (!this.interactionGate?.()) {
      this.interaction?.update(player, this.input);
    }

    const canMove = !this.dialogue.isActive() && player.isMovementEnabled();
    if (canMove) {
      const dir = this.input.getDirection();
      if (dir.x !== 0 || dir.y !== 0) {
        player.move(dir, delta);
      } else {
        player.halt();
      }
    } else if (this.dialogue.isActive()) {
      player.halt();
    }

    player.updateLabel();
    this.debugOverlay?.update(player, this.score, this.quest);
    this.savePlayerProgress(false);
  }

  setMobileControlsVisible(visible: boolean): void {
    this.mobileControls?.setVisible(visible);
  }

  setHudVisible(visible: boolean): void {
    this.scoreHUD?.setVisible(visible);
    this.questPanel?.setVisible(visible);
    setZoneHeaderVisible(visible);
  }

  destroy(): void {
    SaveSystem.saveShutdownIfCurrentScene(
      this.scene.scene.key,
      this.currentZone,
      this.player ? { x: this.player.x, y: this.player.y - 40 } : undefined,
    );
    this.dialogue.destroy();
    this.questPanel?.destroy();
    this.questPanel = undefined;
    this.scoreHUD?.destroy();
    this.mobileControls?.destroy();
    this.debugOverlay?.destroy();
    this.interaction?.destroy();
    this.player?.destroy();
  }

  private savePlayerProgress(force: boolean): void {
    if (SaveSystem.isZoneRestartPending()) return;
    if (!this.player) return;
    const now = this.scene.time.now;
    const movedEnough = this.lastSavedX === undefined
      || this.lastSavedY === undefined
      || Math.abs(this.player.x - this.lastSavedX) >= 20
      || Math.abs(this.player.y - this.lastSavedY) >= 20;
    if (!force) {
      if (!movedEnough) return;
      if (now - this.lastAutosaveTime < 1200) return;
    }

    this.lastAutosaveTime = now;
    this.lastSavedX = this.player.x;
    this.lastSavedY = this.player.y;
    SaveSystem.saveProgress(this.scene.scene.key, this.currentZone, {
      x: this.player.x,
      y: this.player.y - 40,
    });
  }
}
